import React, { useState } from 'react';
import { Bell, TrendingUp, TrendingDown, AlertCircle, X } from 'lucide-react';
import { Stock, PriceAlert } from '../types';

interface StockHeaderProps {
  stock: Stock;
  alerts: PriceAlert[];
  onCreateAlert: (alert: Omit<PriceAlert, 'id' | 'createdAt'>) => void;
  onDeleteAlert: (alertId: string) => void;
}

const StockHeader: React.FC<StockHeaderProps> = ({
  stock,
  alerts,
  onCreateAlert,
  onDeleteAlert
}) => {
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertPrice, setAlertPrice] = useState('');
  const [alertCondition, setAlertCondition] = useState<'above' | 'below'>('above');

  const handleCreateAlert = (e: React.FormEvent) => {
    e.preventDefault();
    const price = parseFloat(alertPrice);
    if (price > 0) {
      onCreateAlert({
        symbol: stock.symbol,
        targetPrice: price,
        condition: alertCondition,
        isActive: true
      });
      setAlertPrice('');
      setShowAlertModal(false);
    }
  };

  const formatMarketCap = (marketCap: number) => {
    if (marketCap >= 1e12) return `$${(marketCap / 1e12).toFixed(2)}T`;
    if (marketCap >= 1e9) return `$${(marketCap / 1e9).toFixed(2)}B`;
    if (marketCap >= 1e6) return `$${(marketCap / 1e6).toFixed(2)}M`;
    return `$${marketCap.toFixed(0)}`;
  };

  const formatVolume = (volume: number) => {
    if (volume >= 1e9) return `${(volume / 1e9).toFixed(2)}B`;
    if (volume >= 1e6) return `${(volume / 1e6).toFixed(2)}M`;
    if (volume >= 1e3) return `${(volume / 1e3).toFixed(2)}K`;
    return volume.toString();
  };

  const stockAlerts = alerts.filter(alert => alert.symbol === stock.symbol && alert.isActive);

  return (
    <>
      <div className="terminal-card p-6">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="flex items-center gap-4 mb-2">
              <h1 className="text-3xl font-bold text-terminal-text">{stock.symbol}</h1>
              <button
                onClick={() => setShowAlertModal(true)}
                className="relative p-2 rounded-lg bg-terminal-border hover:bg-terminal-muted/20 text-terminal-muted hover:text-neon-yellow transition-colors"
              >
                <Bell size={20} />
                {stockAlerts.length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-neon-red text-black text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                    {stockAlerts.length}
                  </span>
                )}
              </button>
            </div>
            <p className="text-terminal-muted text-lg mb-4">{stock.name}</p>
            
            <div className="flex items-baseline gap-4">
              <span className="text-4xl font-bold text-terminal-text">
                ${stock.price.toFixed(2)}
              </span>
              <div className={`flex items-center gap-2 ${
                stock.change >= 0 ? 'text-neon-green' : 'text-neon-red'
              }`}>
                {stock.change >= 0 ? <TrendingUp size={24} /> : <TrendingDown size={24} />}
                <span className="text-xl font-semibold">
                  {stock.change >= 0 ? '+' : ''}${stock.change.toFixed(2)}
                </span>
                <span className="text-xl font-semibold">
                  ({stock.changePercent >= 0 ? '+' : ''}{stock.changePercent.toFixed(2)}%)
                </span>
              </div>
            </div>
          </div>

          {/* Key Metrics */}
          <div className="grid grid-cols-2 gap-6 text-right">
            <div>
              <p className="text-terminal-muted text-sm">Market Cap</p>
              <p className="text-terminal-text font-semibold">
                {stock.marketCap ? formatMarketCap(stock.marketCap) : 'N/A'}
              </p>
            </div>
            <div>
              <p className="text-terminal-muted text-sm">Volume</p>
              <p className="text-terminal-text font-semibold">
                {formatVolume(stock.volume)}
              </p>
            </div>
            <div>
              <p className="text-terminal-muted text-sm">P/E Ratio</p>
              <p className="text-terminal-text font-semibold">
                {stock.pe ? stock.pe.toFixed(2) : 'N/A'}
              </p>
            </div>
            <div>
              <p className="text-terminal-muted text-sm">52W Range</p>
              <p className="text-terminal-text font-semibold text-xs">
                ${stock.low52Week?.toFixed(2)} - ${stock.high52Week?.toFixed(2)}
              </p>
            </div>
          </div>
        </div>

        {/* Active Alerts */}
        {stockAlerts.length > 0 && (
          <div className="border-t border-terminal-border pt-4">
            <h3 className="text-sm font-semibold text-terminal-text mb-2 flex items-center gap-2">
              <AlertCircle size={16} />
              Active Price Alerts
            </h3>
            <div className="flex flex-wrap gap-2">
              {stockAlerts.map((alert) => (
                <div
                  key={alert.id}
                  className="flex items-center gap-2 px-3 py-1 bg-neon-yellow/10 border border-neon-yellow/30 rounded-lg text-sm"
                >
                  <span className="text-neon-yellow">
                    {alert.condition === 'above' ? '↗' : '↘'} ${alert.targetPrice.toFixed(2)}
                  </span>
                  <button
                    onClick={() => onDeleteAlert(alert.id)}
                    className="text-terminal-muted hover:text-neon-red transition-colors"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Price Alert Modal */}
      {showAlertModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="terminal-card p-6 w-96 max-w-[90vw]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-terminal-text">Create Price Alert</h3>
              <button
                onClick={() => setShowAlertModal(false)}
                className="text-terminal-muted hover:text-terminal-text transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleCreateAlert} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-terminal-text mb-2">
                  Alert when {stock.symbol} goes:
                </label>
                <div className="flex gap-2 mb-3">
                  <button
                    type="button"
                    onClick={() => setAlertCondition('above')}
                    className={`flex-1 py-2 px-4 rounded-lg transition-colors ${
                      alertCondition === 'above'
                        ? 'bg-neon-green/20 text-neon-green border border-neon-green/50'
                        : 'bg-terminal-border text-terminal-muted hover:bg-terminal-muted/20'
                    }`}
                  >
                    Above
                  </button>
                  <button
                    type="button"
                    onClick={() => setAlertCondition('below')}
                    className={`flex-1 py-2 px-4 rounded-lg transition-colors ${
                      alertCondition === 'below'
                        ? 'bg-neon-red/20 text-neon-red border border-neon-red/50'
                        : 'bg-terminal-border text-terminal-muted hover:bg-terminal-muted/20'
                    }`}
                  >
                    Below
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-terminal-text mb-2">
                  Target Price
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-terminal-muted">
                    $
                  </span>
                  <input
                    type="number"
                    step="0.01"
                    value={alertPrice}
                    onChange={(e) => setAlertPrice(e.target.value)}
                    placeholder="0.00"
                    className="w-full pl-8 pr-4 py-2 bg-terminal-bg border border-terminal-border rounded-lg text-terminal-text placeholder-terminal-muted focus:outline-none focus:border-neon-blue"
                    required
                  />
                </div>
                <p className="text-xs text-terminal-muted mt-1">
                  Current price: ${stock.price.toFixed(2)}
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 py-2 px-4 bg-neon-blue/20 hover:bg-neon-blue/30 text-neon-blue rounded-lg transition-colors font-medium"
                >
                  Create Alert
                </button>
                <button
                  type="button"
                  onClick={() => setShowAlertModal(false)}
                  className="flex-1 py-2 px-4 bg-terminal-border hover:bg-terminal-muted/20 text-terminal-muted rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default StockHeader;